import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardApi } from "@/services/api";

// Amber-toned palette for service types
const AMBER_PALETTE = [
  "#f59e0b", "#d97706", "#b45309", "#92400e",
  "#fbbf24", "#fcd34d", "#fde68a", "#78350f",
];

interface ServiceItem {
  service: string;
  count: number;
  color: string;
}

export default function ServiceChart() {
  const navigate = useNavigate();
  const [data, setData] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        const result = await dashboardApi.getStats();
        if (result?.serviceDistribution && Array.isArray(result.serviceDistribution)) {
          // Override colors with amber palette
          const colored = result.serviceDistribution.map((s: ServiceItem, i: number) => ({
            ...s,
            color: AMBER_PALETTE[i % AMBER_PALETTE.length],
          }));
          setData(colored);
        } else {
          setData([]);
        }
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServiceData();
  }, []);

  const total = data.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div
      onClick={() => navigate("/reports?section=jobs")}
      className="bg-white rounded-xl border border-amber-100/60 p-4 md:p-5 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer group"
    >
      {/* Header */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-base">Service Distribution</h3>
        <p className="text-xs text-gray-500 mt-0.5">Jobs by service type</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center justify-between mb-1">
                <div className="h-3 rounded w-32" style={{ background: "#fef3c7" }} />
                <div className="h-3 rounded w-12" style={{ background: "#fef3c7" }} />
              </div>
              <div className="h-2 rounded-full" style={{ background: "#fef3c7" }} />
            </div>
          ))}
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: "#fef3c7" }}>
            <i className="ri-pie-chart-line text-xl" style={{ color: "#d97706" }} />
          </div>
          <p className="text-sm text-gray-500">No service data available</p>
        </div>
      ) : (
        <>
          {/* Stacked bar */}
          <div className="flex rounded-full overflow-hidden h-3 mb-4 gap-0.5">
            {data.map((s, i) => {
              const pct = (s.count / total) * 100;
              return (
                <div
                  key={s.service}
                  className="h-full rounded-full transition-all cursor-pointer"
                  style={{
                    width: `${pct}%`,
                    background: s.color,
                    opacity: hoveredIdx === null || hoveredIdx === i ? 1 : 0.4,
                    minWidth: pct > 0 ? "4px" : "0",
                  }}
                  title={`${s.service}: ${s.count}`}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  onClick={(e) => { e.stopPropagation(); navigate("/reports?section=jobs"); }}
                />
              );
            })}
          </div>

          {/* Bars */}
          <div className="space-y-2.5">
            {data.map((s, i) => (
              <div
                key={s.service}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={(e) => { e.stopPropagation(); navigate("/reports?section=jobs"); }}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    <span className="text-xs text-gray-700 font-medium truncate">{s.service}</span>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                    {s.count} <span className="text-gray-400">({Math.round((s.count / total) * 100)}%)</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#fef3c7" }}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${(s.count / total) * 100}%`,
                      background: s.color,
                      opacity: hoveredIdx === null || hoveredIdx === i ? 1 : 0.5,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 flex items-center justify-between" style={{ borderTop: "1px solid #fef3c7" }}>
        <span className="text-xs text-gray-500">Total Jobs</span>
        <span className="text-lg font-bold" style={{ color: "#d97706" }}>{total}</span>
      </div>
    </div>
  );
}
